import {
  Col, Divider, Image, Row,
} from 'antd';
import { Field } from '@ynomia/core/dist/blueprint';
import React from 'react';
import { filtrexHelpers } from '@ynomia/core/dist/utils';
import { getFieldDisplayText, isFieldVisible } from '../../../utils';
import { ImagePreview } from '..';
import { Asset } from '../../../config/types/assets';
import styles from './styles.module.less';
import { SearchHighlight } from '../../connected';

export interface Props {
  schema: Field[];
  values: { [fieldId: string]: any };
  asset?: Asset;
  display?: {
    gutter?: [number, number];
    labelColSpan?: number;
    valueColSpan?: number;
    valueColFlex?: string;
  };
}

/**
 * Use this component wherever you want to display (read only) the value of Ynomia Fields,
 * such as showing asset properties, observation values and anything else that uses our
 * standardised `Field` schema.
 *
 * @param {Props} props
 * @param {Field[]} props.schema: The schema of the field set that should be displayed.
 * @param {object} props.value: Raw field values (corresponding to the field schema).
 * @param {Asset?} props.asset: If the fields somehow relate to an asset, pass this in.
 * This can then be used by Filtrex props for more advanced showing/hiding of fields.
 * @param {object?} props.display
 * @param {[number, number]?} props.display.gutter: Padding for the row gutter.
 * @param {number?} props.display.labelColSpan: How wide should the label column be?
 * @param {number?} props.display.valueColSpan: How wide should the value column be?
 * @param {string?} props.display.valueColFlex: Custom `flex` value for the value column.
 */
const FieldValues: React.FC<Props> = ({
  schema,
  values,
  asset,
  display,
}) => {
  const renderFieldValue = (field: Field) => {
    const value = values[field.id];
    if (value === undefined || value === null || value === '') return <span />;
    // Shared with asset search, so search matches exactly what this pane displays
    const displayText = getFieldDisplayText(field, value) ?? '';

    if (field.fieldType === 'text' && field.properties?.url && filtrexHelpers.isUrl(value)) {
      return (
        <a
          href={value}
          target="_blank"
          className={styles.link}
          rel="noreferrer"
        >
          <SearchHighlight text={displayText} />
        </a>
      );
    }
    if (field.fieldType === 'photo') {
      return (
        <Image.PreviewGroup>
          <div style={{ display: 'flex' }}>
            {value?.map(
              (src: string) => <ImagePreview key={src} src={src} useYnomiaAccessToken />,
            )}
          </div>
        </Image.PreviewGroup>
      );
    }
    if (field.fieldType === 'checkbox') {
      return <span>{displayText}</span>;
    }
    return <SearchHighlight text={displayText} />;
  };

  // Shared with asset search, so search only matches fields this pane displays
  const filteredFields = schema.filter(field => isFieldVisible(field, values, asset || {}));

  return (
    <span>
      {filteredFields.map(field => (field.fieldType === 'divider'
        ? <Divider dashed key={field.id} />
        : (
          <Row gutter={display?.gutter} key={field.id} className={styles.fieldsContainer}>
            <Col span={display?.labelColSpan}>
              <span>{field.label}</span>
            </Col>
            <Col
              span={display?.valueColSpan}
              flex={display?.valueColFlex}
              style={{ textAlign: 'right' }}
            >
              {renderFieldValue(field)}
            </Col>
          </Row>
        )))}
    </span>
  );
};

export default FieldValues;
